import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, Search, AlertCircle,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  CheckSquare, EyeOff, CheckCircle2
} from 'lucide-react';
import toast from 'react-hot-toast';
import AppLayout from '../layout/AppLayout';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { COLORS } from '../../utils/constants';

const C = COLORS;

const ConfigPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canManageItem = (item) => isAdmin || item.created_by === user?.id;

  const location = useLocation();
  const pathParts = location.pathname.split('/');
  const type = pathParts[pathParts.length - 1]; // 'board', 'class', 'subject', 'topic', 'sub-topic'

  const titleMap = {
    board: 'Boards',
    exam: 'Exams',
    class: 'Classes',
    subject: 'Subjects',
    topic: 'Topics',
    'sub-topic': 'Sub Topics'
  };
  const title = titleMap[type] || 'The Advansity';
  const isTopicOrSubTopic = type === 'topic' || type === 'sub-topic';
  const singularLabel = titleMap[type]?.slice(0, -1) || 'Item';
  const pluralLabel = titleMap[type]?.toLowerCase() || 'items';

  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [listTab, setListTab] = useState('Board Exams');
  
  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', status: 'active', subject_id: '', board_id: '', exam_id: '', class_id: '', topic_id: '', question_type: 'Board Exams' });

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/config/${type}`);
      setItems(res.data);
      
      if (type === 'topic') {
        const [subRes, boardRes, examRes, classRes] = await Promise.all([
          api.get('/config/subject'),
          api.get('/config/board'),
          api.get('/config/exam'),
          api.get('/config/class')
        ]);
        setSubjects(subRes.data.filter(s => s.status === 'active'));
        setBoards(boardRes.data.filter(b => b.status === 'active'));
        setExams(examRes.data.filter(e => e.status === 'active'));
        setClasses(classRes.data.filter(c => c.status === 'active'));
      } else if (type === 'sub-topic') {
        const [topRes, subRes, boardRes, examRes, classRes] = await Promise.all([
          api.get('/config/topic'),
          api.get('/config/subject'),
          api.get('/config/board'),
          api.get('/config/exam'),
          api.get('/config/class')
        ]);
        setTopics(topRes.data.filter(t => t.status === 'active'));
        setSubjects(subRes.data.filter(s => s.status === 'active'));
        setBoards(boardRes.data.filter(b => b.status === 'active'));
        setExams(examRes.data.filter(e => e.status === 'active'));
        setClasses(classRes.data.filter(c => c.status === 'active'));
      }
    } catch (err) {
      toast.error(`Failed to load ${title.toLowerCase()}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [type, title]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIds([]);
  }, [search, type, listTab, statusFilter]);

  const handleOpenModal = (item = null) => {
    if (item) {
      if (!canManageItem(item)) {
        toast.error('You can only edit items that you added.');
        return;
      }
      setEditingItem(item);
      setFormData({ 
        name: item.name, 
        status: item.status, 
        subject_id: item.subject_id || '',
        board_id: item.board_id || '',
        exam_id: item.exam_id || '',
        class_id: item.class_id || '',
        topic_id: item.topic_id || '',
        question_type: item.exam_id ? 'Compititive' : 'Board Exams'
      });
    } else {
      setEditingItem(null);
      setFormData({ 
        name: '', 
        status: 'active', 
        subject_id: '', 
        board_id: '', 
        exam_id: '', 
        class_id: '', 
        topic_id: '', 
        question_type: listTab === 'Compititive' ? 'Compititive' : 'Board Exams' 
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    setFormData({ 
      name: '', 
      status: 'active', 
      subject_id: '', 
      board_id: '', 
      exam_id: '', 
      class_id: '', 
      topic_id: '', 
      question_type: listTab === 'Compititive' ? 'Compititive' : 'Board Exams' 
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    const submitPayload = { ...formData };

    if (type === 'topic') {
      if (formData.question_type === 'Board Exams') {
        if (!formData.board_id) { toast.error('Board is required'); return; }
        submitPayload.exam_id = null;
        if (!formData.class_id) { toast.error('Class is required'); return; }
      } else {
        if (!formData.exam_id) { toast.error('Exam is required'); return; }
        submitPayload.board_id = null;
        if (!formData.class_id) {
          submitPayload.class_id = null;
        }
      }
      if (!formData.subject_id) { toast.error('Subject is required'); return; }
    } else if (type === 'sub-topic') {
      if (!formData.topic_id) { toast.error('Topic is required'); return; }
    }

    try {
      if (editingItem) {
        await api.put(`/config/${type}/${editingItem.id}`, submitPayload);
        toast.success(`${titleMap[type].slice(0, -1)} updated successfully`);
      } else {
        await api.post(`/config/${type}`, submitPayload);
        toast.success(`${titleMap[type].slice(0, -1)} created successfully`);
      }
      if (submitPayload.exam_id || formData.question_type === 'Compititive') {
        setListTab('Compititive');
      } else if (submitPayload.board_id || formData.question_type === 'Board Exams') {
        setListTab('Board Exams');
      }
      handleCloseModal();
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    const item = items.find(x => x.id === id);
    if (item && !canManageItem(item)) {
      toast.error('You can only delete items that you added.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete this ${singularLabel}?`)) return;
    try {
      await api.delete(`/config/${type}/${id}`);
      toast.success('Deleted successfully');
      setSelectedIds(prev => prev.filter(x => x !== id));
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const handleToggleStatus = async (item) => {
    if (!canManageItem(item)) {
      toast.error('You can only modify items you added.');
      return;
    }
    const currentStatus = item.status || 'active';
    const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';
    const actionLabel = newStatus === 'inactive' ? 'Deactivating' : 'Activating';
    const toastId = toast.loading(`${actionLabel} ${singularLabel.toLowerCase()}...`);
    try {
      await api.patch(`/config/${type}/${item.id}/status`, { status: newStatus });
      toast.success(`${singularLabel} ${newStatus === 'inactive' ? 'deactivated' : 'activated'} successfully`, { id: toastId });
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status', { id: toastId });
    }
  };

  const handleBulkStatus = async (status) => {
    if (selectedIds.length === 0) return;
    const isDeactivate = status === 'inactive';
    const actionVerb = isDeactivate ? 'deactivate' : 'activate';

    if (!window.confirm(`Are you sure you want to ${actionVerb} ${selectedIds.length} selected ${pluralLabel}?`)) {
      return;
    }

    setBulkLoading(true);
    const toastId = toast.loading(`${isDeactivate ? 'Deactivating' : 'Activating'} ${selectedIds.length} ${pluralLabel}...`);
    try {
      const res = await api.post(`/config/${type}/bulk-status`, { ids: selectedIds, status });
      toast.success(res.data?.message || `${selectedIds.length} ${pluralLabel} updated`, { id: toastId });
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${actionVerb} ${pluralLabel}`, { id: toastId });
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to permanently delete ${selectedIds.length} selected ${pluralLabel}? This action cannot be undone.`)) {
      return;
    }

    setBulkLoading(true);
    const toastId = toast.loading(`Deleting ${selectedIds.length} ${pluralLabel}...`);
    try {
      const res = await api.post(`/config/${type}/bulk-delete`, { ids: selectedIds });
      toast.success(res.data?.message || `${selectedIds.length} ${pluralLabel} deleted`, { id: toastId });
      setSelectedIds([]);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to delete ${pluralLabel}`, { id: toastId });
    } finally {
      setBulkLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    if (type === 'topic' || type === 'sub-topic') {
      if (listTab === 'Board Exams' && !item.board_id) return false;
      if (listTab === 'Compititive' && !item.exam_id) return false;
    }

    if (statusFilter && (item.status || 'active') !== statusFilter) return false;

    const s = search.toLowerCase();
    if (type === 'topic') {
      return (
        (item.name || '').toLowerCase().includes(s) ||
        (item.board_name || '').toLowerCase().includes(s) ||
        (item.exam_name || '').toLowerCase().includes(s) ||
        (item.class_name || '').toLowerCase().includes(s) ||
        (item.subject_name || '').toLowerCase().includes(s)
      );
    } else if (type === 'sub-topic') {
      return (
        (item.name || '').toLowerCase().includes(s) ||
        (item.topic_name || '').toLowerCase().includes(s) ||
        (item.board_name || '').toLowerCase().includes(s) ||
        (item.exam_name || '').toLowerCase().includes(s) ||
        (item.class_name || '').toLowerCase().includes(s) ||
        (item.subject_name || '').toLowerCase().includes(s)
      );
    }
    return (item.name || '').toLowerCase().includes(s);
  });

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Manageable items for bulk selection
  const manageablePaginated = paginatedItems.filter(item => canManageItem(item));
  const isAllPageSelected = manageablePaginated.length > 0 && manageablePaginated.every(item => selectedIds.includes(item.id));
  const isSomePageSelected = manageablePaginated.some(item => selectedIds.includes(item.id)) && !isAllPageSelected;

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleSelectAllPage = () => {
    if (isAllPageSelected) {
      const pageIds = manageablePaginated.map(item => item.id);
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      const pageIds = manageablePaginated.map(item => item.id);
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleSelectAllFiltered = () => {
    const allFilteredManageableIds = filteredItems.filter(item => canManageItem(item)).map(item => item.id);
    setSelectedIds(allFilteredManageableIds);
  };

  const getPaginationItems = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }
    if (currentPage >= totalPages - 3) {
      return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  return (
    <AppLayout title={title}>
      {(type === 'topic' || type === 'sub-topic') && (
        <div style={{ display: 'flex', gap: 24, marginBottom: 24, borderBottom: `1px solid ${C.g200}` }}>
          {['Board Exams', 'Compititive'].map(tab => (
            <button
              key={tab}
              onClick={() => setListTab(tab)}
              style={{
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                fontSize: 15,
                fontWeight: listTab === tab ? 600 : 500,
                color: listTab === tab ? C.primary : C.g500,
                borderBottom: `2px solid ${listTab === tab ? C.primary : 'transparent'}`,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: -1
              }}
            >
              {tab}
            </button>
          ))}
        </div>
      )}

      {/* Header Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', width: 280 }}>
            <Search size={18} color={C.g400} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '100%', padding: '10px 14px 10px 40px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
            />
          </div>

          {isTopicOrSubTopic && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                border: `1px solid ${C.g200}`,
                fontSize: 14,
                color: statusFilter ? C.g900 : C.g500,
                background: '#fff',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          )}
        </div>

        <button
          onClick={() => handleOpenModal()}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.2)' }}
        >
          <Plus size={18} /> Add {singularLabel}
        </button>
      </div>

      {/* ─── Bulk Action Banner (Visible when items are selected) ─── */}
      {selectedIds.length > 0 && isTopicOrSubTopic && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
          border: '1px solid #C7D2FE',
          borderRadius: 14,
          padding: '14px 20px',
          marginBottom: 16,
          flexWrap: 'wrap',
          gap: 12,
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: C.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckSquare size={18} />
            </div>
            <div>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#1E1B4B' }}>
                {selectedIds.length} {singularLabel.toLowerCase()}{selectedIds.length > 1 ? (type === 'topic' ? 's' : 's') : ''} selected
              </span>
              {filteredItems.length > paginatedItems.length && selectedIds.length === manageablePaginated.length && (
                <button
                  type="button"
                  onClick={handleSelectAllFiltered}
                  style={{ marginLeft: 12, background: 'none', border: 'none', color: C.primary, fontWeight: 600, fontSize: 13, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                >
                  Select all {filteredItems.filter(canManageItem).length} {pluralLabel} matching filters
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Deactivate button */}
            <button
              type="button"
              onClick={() => handleBulkStatus('inactive')}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#FFFBEB',
                color: '#B45309',
                border: '1px solid #FCD34D',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              title={`Deactivate selected ${pluralLabel}`}
            >
              <EyeOff size={15} /> Deactivate Selected
            </button>

            {/* Activate button */}
            <button
              type="button"
              onClick={() => handleBulkStatus('active')}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#ECFDF5',
                color: '#059669',
                border: '1px solid #A7F3D0',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s',
              }}
              title={`Activate selected ${pluralLabel}`}
            >
              <CheckCircle2 size={15} /> Activate Selected
            </button>

            {/* Delete button */}
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                background: '#DC2626',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: bulkLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                transition: 'all 0.15s',
              }}
              title={`Permanently delete selected ${pluralLabel}`}
            >
              <Trash2 size={15} /> Delete Selected
            </button>

            {/* Deselect */}
            <button
              type="button"
              onClick={() => setSelectedIds([])}
              disabled={bulkLoading}
              style={{
                padding: '8px 12px',
                background: '#fff',
                color: C.g600,
                border: `1px solid ${C.g200}`,
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 16, border: `1px solid ${C.g200}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: C.g50, borderBottom: `1px solid ${C.g200}` }}>
                {isTopicOrSubTopic && (
                  <th style={{ padding: '16px 14px', width: 44, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isAllPageSelected}
                      ref={el => { if (el) el.indeterminate = isSomePageSelected; }}
                      onChange={handleSelectAllPage}
                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: C.primary }}
                      title="Select all on this page"
                    />
                  </th>
                )}
                <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>ID</th>
                {(type === 'topic' || type === 'sub-topic') && (
                  <>
                    <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Board / Exam</th>
                    <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Class</th>
                    <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Subject</th>
                  </>
                )}
                {type === 'sub-topic' && (
                  <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Topic</th>
                )}
                <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>
                  {type === 'sub-topic' ? 'Sub Topic Detail' : 'Name'}
                </th>
                <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase' }}>Status</th>
                <th style={{ padding: '14px 24px', fontSize: 12, fontWeight: 600, color: C.g500, textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={type === 'sub-topic' ? 9 : type === 'topic' ? 8 : 4} style={{ padding: 40, textAlign: 'center', color: C.g400 }}>Loading...</td></tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={type === 'sub-topic' ? 9 : type === 'topic' ? 8 : 4} style={{ padding: 40, textAlign: 'center', color: C.g500 }}>
                    <AlertCircle size={32} color={C.g300} style={{ margin: '0 auto 12px' }} />
                    <p style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>No items found</p>
                    <p style={{ margin: '4px 0 0', fontSize: 13, color: C.g400 }}>Try adjusting your search or add a new one.</p>
                  </td>
                </tr>
              ) : (
                paginatedItems.map(item => {
                  const isChecked = selectedIds.includes(item.id);
                  const isInactive = item.status === 'inactive';
                  const isManageable = canManageItem(item);

                  return (
                    <tr 
                      key={item.id} 
                      style={{ 
                        borderBottom: `1px solid ${C.g100}`,
                        background: isChecked ? '#F5F7FF' : 'transparent',
                        transition: 'background 0.15s ease',
                      }}
                    >
                      {isTopicOrSubTopic && (
                        <td style={{ padding: '16px 14px', textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            disabled={!isManageable}
                            onChange={() => handleToggleSelect(item.id)}
                            style={{
                              width: 16,
                              height: 16,
                              cursor: isManageable ? 'pointer' : 'not-allowed',
                              accentColor: C.primary,
                            }}
                            title={isManageable ? `Select ${singularLabel.toLowerCase()}` : `You do not have permission to modify this ${singularLabel.toLowerCase()}`}
                          />
                        </td>
                      )}
                      <td style={{ padding: '16px 24px', fontSize: 14, color: C.g500 }}>#{item.id}</td>
                      {(type === 'topic' || type === 'sub-topic') && (
                        <>
                          <td style={{ padding: '16px 24px', fontSize: 14, color: C.g600 }}>{item.exam_name || item.board_name || '-'}</td>
                          <td style={{ padding: '16px 24px', fontSize: 14, color: C.g600 }}>{item.class_name || '-'}</td>
                          <td style={{ padding: '16px 24px', fontSize: 14, color: C.g600 }}>{item.subject_name || '-'}</td>
                        </>
                      )}
                      {type === 'sub-topic' && (
                        <td style={{ padding: '16px 24px', fontSize: 14, color: C.g600 }}>{item.topic_name || '-'}</td>
                      )}
                      <td style={{ padding: '16px 24px', fontSize: 14, fontWeight: 500, color: C.g900 }}>{item.name}</td>
                      <td style={{ padding: '16px 24px' }}>
                        <span style={{
                          display: 'inline-block', padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: item.status === 'active' ? '#ECFDF5' : '#FEF2F2',
                          color: item.status === 'active' ? '#059669' : '#DC2626'
                        }}>
                          {item.status ? (item.status.charAt(0).toUpperCase() + item.status.slice(1)) : 'Active'}
                        </span>
                      </td>
                      <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                          {canManageItem(item) ? (
                            <>
                              {isTopicOrSubTopic && (
                                <button
                                  onClick={() => handleToggleStatus(item)}
                                  style={{
                                    padding: 6,
                                    background: isInactive ? '#ECFDF5' : '#FFFBEB',
                                    border: 'none',
                                    borderRadius: 6,
                                    color: isInactive ? '#059669' : '#D97706',
                                    cursor: 'pointer'
                                  }}
                                  title={isInactive ? `Activate ${singularLabel.toLowerCase()}` : `Deactivate ${singularLabel.toLowerCase()}`}
                                >
                                  {isInactive ? <CheckCircle2 size={16} /> : <EyeOff size={16} />}
                                </button>
                              )}
                              <button onClick={() => handleOpenModal(item)} style={{ padding: 6, background: C.g50, border: 'none', borderRadius: 6, color: C.g600, cursor: 'pointer' }} title="Edit">
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => handleDelete(item.id)} style={{ padding: 6, background: '#FEF2F2', border: 'none', borderRadius: 6, color: C.danger, cursor: 'pointer' }} title="Delete">
                                <Trash2 size={16} />
                              </button>
                            </>
                          ) : (
                            <span style={{ fontSize: 12, color: C.g500, background: C.g100, padding: '4px 10px', borderRadius: 12, fontWeight: 500 }}>
                              View only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Records Per Page Bar */}
        <div style={{
          padding: '16px 24px',
          borderTop: `1px solid ${C.g200}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          background: '#fff'
        }}>
          {/* Records per page & info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, color: C.g600, fontWeight: 500 }}>Records per page:</span>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  fontSize: 13,
                  fontWeight: 600,
                  color: C.g800,
                  background: '#fff',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span style={{ fontSize: 13, color: C.g500 }}>
              Showing {filteredItems.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredItems.length)} of {filteredItems.length} entries
            </span>
          </div>

          {/* Pagination Navigation */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {/* First Page button */}
              <button
                type="button"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                title="First page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === 1 ? C.g300 : C.g700,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsLeft size={16} />
              </button>

              {/* Previous button */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                title="Previous page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === 1 ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronLeft size={16} /> Prev
              </button>

              {/* Page numbers with ellipsis */}
              {getPaginationItems().map((item, index) => {
                if (item === '...') {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      style={{
                        width: 28,
                        textAlign: 'center',
                        color: C.g400,
                        fontSize: 13,
                        fontWeight: 600,
                        userSelect: 'none'
                      }}
                    >
                      …
                    </span>
                  );
                }

                const isCurrent = item === currentPage;
                return (
                  <button
                    key={`page-${item}`}
                    type="button"
                    onClick={() => setCurrentPage(item)}
                    style={{
                      minWidth: 34,
                      height: 34,
                      padding: '0 8px',
                      borderRadius: 8,
                      border: `1px solid ${isCurrent ? C.primary : C.g200}`,
                      background: isCurrent ? C.primary : '#fff',
                      color: isCurrent ? '#fff' : C.g700,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {item}
                  </button>
                );
              })}

              {/* Next button */}
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                title="Next page"
                style={{
                  padding: '0 10px',
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === totalPages ? C.g300 : C.g700,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Next <ChevronRight size={16} />
              </button>

              {/* Last Page button */}
              <button
                type="button"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
                style={{
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 8,
                  border: `1px solid ${C.g200}`,
                  background: '#fff',
                  color: currentPage === totalPages ? C.g300 : C.g700,
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', width: '100%', maxWidth: (type === 'topic' || type === 'sub-topic') ? 640 : 480, borderRadius: 20, boxShadow: '0 20px 40px rgba(0,0,0,0.1)', overflow: 'hidden', animation: 'slideUp 0.3s ease-out', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '24px 28px', borderBottom: `1px solid ${C.g100}` }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.g900 }}>
                {editingItem ? 'Edit' : 'Add'} {titleMap[type]?.slice(0, -1)}
              </h3>
            </div>
            
            <form onSubmit={handleSubmit} style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
              {(type === 'topic' || type === 'sub-topic') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <div style={{ marginBottom: 20, gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Question Type</label>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="question_type"
                          value="Board Exams"
                          checked={formData.question_type === 'Board Exams'}
                          onChange={e => setFormData({ ...formData, question_type: e.target.value, exam_id: '' })}
                        />
                        Board Exams
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="question_type"
                          value="Compititive"
                          checked={formData.question_type === 'Compititive'}
                          onChange={e => setFormData({ ...formData, question_type: e.target.value, board_id: '' })}
                        />
                        Compititive
                      </label>
                    </div>
                  </div>

                  {formData.question_type === 'Board Exams' ? (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Board</label>
                      <select
                        value={formData.board_id || ''}
                        onChange={e => setFormData({...formData, board_id: e.target.value})}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                        required={formData.question_type === 'Board Exams'}
                      >
                        <option value="">Select a Board</option>
                        {boards.map(b => (
                          <option key={b.id} value={b.id}>{b.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Exam</label>
                      <select
                        value={formData.exam_id || ''}
                        onChange={e => setFormData({...formData, exam_id: e.target.value})}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                        required={formData.question_type === 'Compititive'}
                      >
                        <option value="">Select an Exam</option>
                        {exams.map(e => (
                          <option key={e.id} value={e.id}>{e.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>
                      Class {formData.question_type === 'Compititive' && <span style={{ fontWeight: 400, color: C.g400 }}>(Optional)</span>}
                    </label>
                    <select
                      value={formData.class_id || ''}
                      onChange={e => setFormData({...formData, class_id: e.target.value})}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                      required={formData.question_type === 'Board Exams'}
                    >
                      <option value="">{formData.question_type === 'Compititive' ? 'Select a Class (Optional)' : 'Select a Class'}</option>
                      {classes.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Subject</label>
                    <select
                      value={formData.subject_id || ''}
                      onChange={e => setFormData({...formData, subject_id: e.target.value})}
                      style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                      required
                    >
                      <option value="">Select a Subject</option>
                      {subjects.map(sub => (
                        <option key={sub.id} value={sub.id}>{sub.name}</option>
                      ))}
                    </select>
                  </div>

                  {type === 'sub-topic' && (
                    <div style={{ marginBottom: 20 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Topic</label>
                      <select
                        value={formData.topic_id || ''}
                        onChange={e => setFormData({...formData, topic_id: e.target.value})}
                        style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                        required
                      >
                        <option value="">Select a Topic</option>
                        {topics
                          .filter(t => {
                            if (formData.question_type === 'Board Exams') {
                              if (t.exam_id) return false;
                              if (formData.board_id && t.board_id != formData.board_id) return false;
                            } else {
                              if (t.board_id) return false;
                              if (formData.exam_id && t.exam_id != formData.exam_id) return false;
                            }
                            if (formData.class_id && t.class_id && t.class_id != formData.class_id) return false;
                            if (formData.subject_id && t.subject_id && t.subject_id != formData.subject_id) return false;
                            return true;
                          })
                          .map(t => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>
                  {type === 'sub-topic' ? 'Sub Topic Detail' : 'Name'}
                </label>
                {type === 'sub-topic' ? (
                  <textarea
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="Enter sub topic details..."
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', minHeight: 100, resize: 'vertical' }}
                    required
                  />
                ) : (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder={`Enter ${titleMap[type]?.slice(0, -1).toLowerCase()} name`}
                    style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none' }}
                    required
                  />
                )}
              </div>

              <div style={{ marginBottom: 28 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.g700, marginBottom: 8 }}>Status</label>
                <select
                  value={formData.status}
                  onChange={e => setFormData({...formData, status: e.target.value})}
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.g200}`, fontSize: 14, outline: 'none', background: '#fff' }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" onClick={handleCloseModal} style={{ flex: 1, padding: '12px', background: C.g100, color: C.g700, border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel
                </button>
                <button type="submit" style={{ flex: 1, padding: '12px', background: C.primary, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                  {editingItem ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </form>
          </div>
          <style>{`@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
      )}
    </AppLayout>
  );
};

export default ConfigPage;
